import { useHandler } from "@redotech/react-util/hook";
import {
  SegmentListMultipleItemMembershipOperator,
  SegmentListSingleItemMembershipOperator,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  rcoProductVendorSegmentSchema,
  RcoProductVendorSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import { OperatorSelector } from "@redotech/redo-web-conversion/conversion/operator-selector";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";
import { ProductVendorSelector } from "./product-vendor-selector";

export const ProductVendorInspectorContent = memo(
  function ProductVendorInspectorContent({ step }: { step: RcoSegmentStep }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoProductVendorSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {operatorsToText(segment.cartOperator, segment.operator)}
          </Text>
        </Flex>
        <Flex dir="row" gap="md" wrap="wrap">
          {segment.vendors.map((vendor, index) => (
            <RedoBadge color="gray" key={index} size="sm" text={vendor} />
          ))}
        </Flex>
      </>
    );
  },
);

const operatorsToText = (
  cartOperator: RcoProductVendorSegmentSchema["cartOperator"],
  operator: RcoProductVendorSegmentSchema["operator"],
) => {
  switch (cartOperator) {
    case SegmentListMultipleItemMembershipOperator.ANY_IN:
      switch (operator) {
        case SegmentListSingleItemMembershipOperator.IN:
          return t(
            "The vendor for at least one product in the cart is in the list:",
          );
        case SegmentListSingleItemMembershipOperator.NOT_IN:
          return t(
            "The vendor for at least one product in the cart is not in the list:",
          );
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.ALL_IN:
      switch (operator) {
        case SegmentListSingleItemMembershipOperator.IN:
          return t("The vendor for every product in the cart is in the list:");
        case SegmentListSingleItemMembershipOperator.NOT_IN:
          return t(
            "The vendor for every product in the cart is not in the list:",
          );
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.NONE_IN:
      switch (operator) {
        case SegmentListSingleItemMembershipOperator.IN:
          return t(
            "The vendor for every product in the cart is not in the list:",
          );
        case SegmentListSingleItemMembershipOperator.NOT_IN:
          return t("The vendor for every product in the cart is in the list:");
        default:
          assertNever(operator);
      }
      break;
    default:
      assertNever(cartOperator);
  }
};

export function segmentListMultipleItemMembershipOperatorToText(): Record<
  SegmentListMultipleItemMembershipOperator,
  string
> {
  return {
    [SegmentListMultipleItemMembershipOperator.ANY_IN]: t("at least one"),
    [SegmentListMultipleItemMembershipOperator.NONE_IN]: "no",
    [SegmentListMultipleItemMembershipOperator.ALL_IN]: "every",
  };
}

export function segmentListSingleItemMembershipOperatorToText(): Record<
  SegmentListSingleItemMembershipOperator,
  string
> {
  return {
    [SegmentListSingleItemMembershipOperator.IN]: "is",
    [SegmentListSingleItemMembershipOperator.NOT_IN]: t("is not"),
  };
}

export const EditableProductVendorInspectorContent = memo(
  function EditableProductVendorInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoProductVendorSegmentSchema.parse(step.segment),
      [step],
    );

    const handleCartOperatorSelected = useHandler(
      (cartOperator: SegmentListMultipleItemMembershipOperator) => {
        const updatedSegment = { ...segment, cartOperator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleOperatorSelected = useHandler(
      (operator: SegmentListSingleItemMembershipOperator) => {
        const updatedSegment = { ...segment, operator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleSetVendors = useHandler((vendors: string[]) => {
      setStep({ ...step, segment: { ...segment, vendors } });
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {t("The vendor for")}
          </Text>
        </Flex>
        <Flex>
          <OperatorSelector<SegmentListMultipleItemMembershipOperator>
            availableOptions={Object.values(
              SegmentListMultipleItemMembershipOperator,
            )}
            onOperatorSelected={handleCartOperatorSelected}
            operatorToText={segmentListMultipleItemMembershipOperatorToText()}
            selectedOperator={segment.cartOperator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("product in the cart")}
        </Text>
        <Flex>
          <OperatorSelector<SegmentListSingleItemMembershipOperator>
            availableOptions={Object.values(
              SegmentListSingleItemMembershipOperator,
            )}
            onOperatorSelected={handleOperatorSelected}
            operatorToText={segmentListSingleItemMembershipOperatorToText()}
            selectedOperator={segment.operator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("in the list:")}
        </Text>
        <ProductVendorSelector
          onVendorsSelected={handleSetVendors}
          selectedVendors={segment.vendors}
        />
      </>
    );
  },
);
